import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Sparkles, RefreshCw, Send, Trash2, ExternalLink } from "lucide-react";
import { HUBS, CONDITIONS } from "@/data/care";

export default function AdminJournal() {
  const [hub, setHub] = useState("aortic");
  const [condition, setCondition] = useState("");
  const [brief, setBrief] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [heroUrl, setHeroUrl] = useState("");
  const [published, setPublished] = useState([]);
  const [stats, setStats] = useState({});

  const hubConds = CONDITIONS.filter((c) => c.hub === hub);

  const loadPublished = async () => {
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/journal"),
        api.get("/admin/journal/stats"),
      ]);
      setPublished(list || []);
      setStats(s || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPublished(); }, []);

  const generate = async () => {
    if (!brief.trim()) return toast.error("Add a short brief for Dr Sidhu's voice to work from.");
    setLoading(true); setDraft(null);
    try {
      const { data } = await api.post("/admin/journal/draft", { hub, condition, brief });
      setDraft(data.draft);
      toast.success("Draft ready — review, edit hero URL, then Publish.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setLoading(false); }
  };

  const updateChapter = (i, k, v) => {
    setDraft((d) => ({ ...d, chapters: d.chapters.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  };

  const publish = async () => {
    if (!draft) return;
    setPublishing(true);
    try {
      const payload = {
        slug: draft.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60),
        title: draft.title,
        deck: draft.deck,
        hub,
        condition: condition || null,
        tags: [hub, condition || "case study"].filter(Boolean),
        readTime: "4 min",
        author: "Dr Tegbir Sidhu",
        hero: heroUrl || "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1600&q=80",
        chapters: draft.chapters,
        lesson: draft.lesson,
      };
      const { data } = await api.post("/admin/journal/publish", payload);
      toast.success(`Published — live at /journal/${data.slug}`);
      loadPublished();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const copyAsJs = async () => {
    if (!draft) return;
    const slug = draft.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60);
    const obj = { slug, title: draft.title, deck: draft.deck, hub, condition: condition || undefined,
      tags: [hub, condition || "case study"].filter(Boolean), readTime: "4 min",
      date: new Date().toISOString().slice(0, 10), author: "Dr Tegbir Sidhu",
      hero: heroUrl || "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1600&q=80",
      chapters: draft.chapters, lesson: draft.lesson };
    await navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
    toast.success("Copied — paste into journal.js for git-tracked storage.");
  };

  const unpublish = async (slug) => {
    if (!window.confirm(`Unpublish '${slug}'?`)) return;
    try {
      await api.delete(`/admin/journal/${slug}`);
      toast.success("Unpublished");
      loadPublished();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-journal">
      <div className="kbd">— Journal drafting & publishing</div>
      <h1 className="mt-2 font-serif text-4xl tracking-tightest">Case studies</h1>
      <p className="mt-3 text-ink-muted max-w-2xl text-sm">
        Brief → Claude drafts → you edit → <span className="text-ink font-medium">Publish</span> pushes live to <code className="text-xs bg-black/5 px-1 rounded">/journal</code> instantly (no code deploy).
      </p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="hairline p-6 bg-white/70 space-y-4">
          <div>
            <Label className="kbd">Hub</Label>
            <Select value={hub} onValueChange={(v) => { setHub(v); setCondition(""); }}>
              <SelectTrigger data-testid="draft-hub-select" className="mt-2"><SelectValue/></SelectTrigger>
              <SelectContent>{HUBS.map((h)=><SelectItem key={h.slug} value={h.slug}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="kbd">Condition (optional)</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="draft-condition-select" className="mt-2"><SelectValue placeholder="Any in hub"/></SelectTrigger>
              <SelectContent>{hubConds.map((c)=><SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="kbd">Brief</Label>
            <Textarea data-testid="draft-brief" value={brief} onChange={(e)=>setBrief(e.target.value)} rows={6} className="mt-2 text-sm" placeholder="Age, city, referral, imaging, decision, outcome — 2 to 4 sentences."/>
          </div>
          <div>
            <Label className="kbd">Hero image URL (optional)</Label>
            <Input data-testid="draft-hero" value={heroUrl} onChange={(e)=>setHeroUrl(e.target.value)} placeholder="https://images.unsplash.com/..." className="mt-2 text-sm"/>
          </div>
          <button onClick={generate} data-testid="draft-generate" disabled={loading} className="btn-pill-inv w-full justify-center disabled:opacity-60">
            {loading ? <><RefreshCw size={14} className="animate-spin"/> Drafting…</> : <><Sparkles size={14}/> Draft with Claude</>}
          </button>
        </div>

        <div className="hairline p-6 bg-white/70">
          {!draft && !loading && (
            <div className="text-center py-16 text-ink-muted">
              <Sparkles size={20} className="mx-auto text-crimson"/>
              <div className="mt-4 kbd">Awaiting brief</div>
            </div>
          )}
          {loading && <div className="py-16 text-center kbd">Claude is drafting…</div>}
          {draft && (
            <div data-testid="draft-output" className="space-y-5">
              <div>
                <Label className="kbd">Title</Label>
                <Input value={draft.title} onChange={(e)=>setDraft({...draft, title: e.target.value})} className="mt-1 font-serif text-2xl border-0 border-b border-ink/30 rounded-none bg-transparent focus-visible:ring-0 px-0"/>
              </div>
              <div>
                <Label className="kbd">Deck</Label>
                <Textarea value={draft.deck} onChange={(e)=>setDraft({...draft, deck: e.target.value})} rows={2} className="mt-1 italic text-sm"/>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {(draft.chapters || []).map((c, i) => (
                  <div key={c.n} className="hairline-t pt-3">
                    <div className="kbd">Chapter {c.n} · {c.title}</div>
                    <Textarea value={c.body} onChange={(e)=>updateChapter(i, "body", e.target.value)} rows={3} className="mt-2 text-sm"/>
                  </div>
                ))}
              </div>
              <div>
                <Label className="kbd">Lesson</Label>
                <Textarea value={draft.lesson} onChange={(e)=>setDraft({...draft, lesson: e.target.value})} rows={2} className="mt-1 italic text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={publish} data-testid="draft-publish" disabled={publishing} className="btn-pill-inv justify-center disabled:opacity-60">
                  {publishing ? "Publishing…" : <><Send size={14}/> Publish live</>}
                </button>
                <button onClick={copyAsJs} data-testid="draft-copy" className="btn-pill justify-center">
                  <Copy size={14}/> Copy as JS
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {published.length > 0 && (
        <div className="mt-16" data-testid="published-list">
          <h2 className="font-serif text-2xl tracking-tightest">Published articles</h2>
          <div className="mt-4 hairline bg-white/70 divide-y divide-black/10">
            {published.map((p) => (
              <div key={p.slug} data-testid={`published-${p.slug}`} className="p-4 grid grid-cols-12 gap-3 items-center">
                <div className="col-span-12 md:col-span-5">
                  <div className="font-serif text-lg tracking-tightest">{p.title}</div>
                  <div className="kbd mt-1">/journal/{p.slug}</div>
                </div>
                <div className="col-span-6 md:col-span-2 kbd">{p.hub || "—"}</div>
                <div className="col-span-6 md:col-span-2 flex gap-4 text-xs" data-testid={`stats-${p.slug}`}>
                  <span title="Views">👁 {stats[p.slug]?.view || 0}</span>
                  <span title="Second-opinion clicks" className="text-crimson">↗ {stats[p.slug]?.so_click || 0}</span>
                </div>
                <div className="col-span-12 md:col-span-3 flex gap-3 justify-end text-sm">
                  <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink inline-flex items-center gap-1"><ExternalLink size={12}/> View</a>
                  <button onClick={() => unpublish(p.slug)} data-testid={`unpublish-${p.slug}`} className="text-crimson hover:text-crimson/70 inline-flex items-center gap-1"><Trash2 size={12}/> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
